import React, { useEffect, useRef, useState } from "react";
import { Alert, Form, Input, Modal, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  useGetPendingInvitationsByPartnerQuery,
  useInvitePartnerUserMutation,
} from "../../graphql/generated/hooks";

const InvitePartnerUserModal: React.FC<{
  isOpen: boolean;
  close: Function;
  partnerId: number;
}> = ({ isOpen, close, partnerId }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [inviteUser, { data, loading, reset }] = useInvitePartnerUserMutation({
    refetchQueries: ["GetPendingInvitationsByPartner"],
  });
  // PartnerList resets partnerId to 0 on close; don't query for it.
  const { data: invitationsData, loading: invitationsLoading } =
    useGetPendingInvitationsByPartnerQuery({
      variables: { partnerId },
      skip: !isOpen || !partnerId,
      fetchPolicy: "cache-and-network",
    });
  const hasOpenedOnceRef = useRef(false);
  const [inviteErrorMessage, setInviteErrorMessage] = useState<string | null>(
    null,
  );

  const invitePortalBaseUrl = (
    process.env.REACT_APP_PARTNER_PORTAL_URL || "https://partner.stg.robinfood.dev/"
  ).replace(/\/+$/, "");

  useEffect(() => {
    if (isOpen) {
      hasOpenedOnceRef.current = true;
      return;
    }

    // Avoid calling resetFields before the Form is ever mounted/connected.
    if (hasOpenedOnceRef.current) {
      form.resetFields();
      reset?.();
      setInviteErrorMessage(null);
    }
  }, [isOpen, form, reset]);

  const onFinish = async (values: any) => {
    // Ensure we don't show a previous invite link when re-inviting.
    reset?.();
    setInviteErrorMessage(null);

    try {
      await inviteUser({
        variables: {
          partnerId,
          email: values.email,
        },
      });
    } catch (error: any) {
      const gqlError = error?.graphQLErrors?.[0];
      const i18nKey: string | undefined = gqlError?.extensions?.i18nKey;
      const translated = i18nKey ? t(i18nKey) : "";

      setInviteErrorMessage(
        (i18nKey && translated && translated !== i18nKey
          ? translated
          : gqlError?.message) || t("message.error.generic"),
      );
    }
  };

  return (
    <Modal
      title={t("title.invite_user")}
      open={isOpen}
      onOk={() => {
        form.submit();
      }}
      onCancel={() => {
        close();
        form.resetFields();
        reset?.();
        setInviteErrorMessage(null);
      }}
      confirmLoading={loading}
    >
      {inviteErrorMessage && (
        <Alert
          message={inviteErrorMessage}
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {data && (
        <Alert
          message={t("message.success.invitation_sent")}
          description={
            <a
              href={`${invitePortalBaseUrl}/invite/${data.invitePartnerUser}`}
              target="_blank"
              rel="noreferrer"
            >
              {`${invitePortalBaseUrl}/invite/${data.invitePartnerUser}`}
            </a>
          }
          type="success"
          showIcon
          closable
          onClose={() => {
            close();
            form.resetFields();
            reset?.();
            setInviteErrorMessage(null);
          }}
        />
      )}
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        style={{ marginTop: "20px" }}
      >
        <Form.Item
          rules={[
            { required: true, message: t("message.error.field_required") },
            { type: "email", message: t("message.error.invalid_email") },
          ]}
          hasFeedback
          name="email"
          label={t("label.email")}
        >
          <Input />
        </Form.Item>
      </Form>
      <Typography.Title level={5}>
        {t("title.pending_invitations")}
      </Typography.Title>
      <Table
        size="small"
        rowKey="email"
        pagination={false}
        loading={invitationsLoading}
        dataSource={invitationsData?.getPendingInvitationsByPartner ?? []}
        columns={[
          { title: t("label.email"), dataIndex: "email", ellipsis: true },
          {
            title: t("label.store.name"),
            dataIndex: "storeName",
            render: (value?: string | null) =>
              value || t("label.all_stores_access"),
          },
          {
            title: t("label.created_at"),
            dataIndex: "createdAt",
            render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm"),
          },
        ]}
      />
    </Modal>
  );
};

export default InvitePartnerUserModal;
